import orderBy from 'lodash/orderBy';
import { Box, Spinner } from 'native-base';
import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform } from 'react-native';

import Carousel from 'app/components/Carousel';
import ResponsiveContainer from 'app/components/ResponsiveContainer';
import ReservationTicket from 'app/components/TicketCarousel/ReservationTicket';
import Ticket from 'app/components/TicketCarousel/Ticket';
import TicketsCarouselPagination from 'app/components/TicketCarousel/TicketCarouselPagination';
import useAttraction from 'app/hooks/useAttraction';
import useConfiguration from 'app/hooks/useConfiguration';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import useWindow from 'app/hooks/useWindow';
import { useI18n } from 'app/providers/I18nProvider';
import { getPassCountByClass } from 'app/services/EntitlementHelper';
import {
  EntitlementReservation,
  Pass,
  TicketCategory,
  TicketClass,
} from 'app/services/GuestCenterService.types';
import { tID } from 'app/services/TestHelper';
import {
  createReservationTicket,
  createTicket,
  getAddonLabel,
  isAddonTicket,
} from 'app/services/TicketHelper';
import { MAX_WIDTH_WEB } from 'app/theme/breakpoints';

export type TicketCarouselProps = {
  passes: Pass[];
  reservation?: EntitlementReservation;
  orderNumber?: string;
  ticketCategory?: TicketCategory;
  attractionKey?: string;
};

export default function TicketCarousel({
  passes,
  reservation,
  orderNumber,
  ticketCategory,
  attractionKey,
}: TicketCarouselProps) {
  const { rawWidth } = useWindow();
  const { data: productLineups = [] } = useConfiguration({
    select: (res) => res.productLineups.configuration,
  });
  const [maxTicketHeight, setMaxTicketHeight] = useState(0);

  const productContentKey =
    productLineups.find((pl) => pl.productLineupKey === passes[0]?.productLineupKey)?.contentKey ??
    '';
  const attractionContentKey = reservation?.attractionConfig.contentKey ?? '';
  const { attractionContent, productContent, attractionConfig } = useAttraction({
    productContentKey,
    attractionContentKey,
  });

  const rt = useReservationTextTranslationHelper({
    product: productContent,
    attraction: attractionContent,
  });
  const { t } = useI18n();

  const showOrderBarcode = reservation?.attractionConfig.ticketingSystem?.showOrderBarcode;

  const reservationTickets = useMemo(() => {
    if (!reservation?.tickets) return [];
    if (!ticketCategory?.categoryKey) return reservation.tickets;
    return reservation.tickets.filter(
      (ticket) => ticket.categoryKey === ticketCategory.categoryKey
    );
  }, [reservation, ticketCategory]);

  // Get tickets from reservation or create from passes when no reservation
  const allTickets = useMemo(() => {
    // When there's a reservation with tickets, use those
    if (reservationTickets.length > 0) {
      let ticketsToShow = reservationTickets;

      // If showOrderBarcode, only show first ticket
      if (showOrderBarcode && ticketsToShow.length > 0) {
        ticketsToShow = [ticketsToShow[0]];
      }

      // Map tickets with their corresponding pass
      const ticketsWithPass = ticketsToShow.map((ticket) => ({
        ticket,
        pass: ticket.passBarcode ? passes.find((p) => p.barcode === ticket.passBarcode) : undefined,
      }));

      // Sort: regular tickets (with pass) first, then addons
      // Within each group, sort adults first, then children, then by name
      return orderBy(ticketsWithPass, [
        // Primary sort: addons last
        (item) => {
          if (isAddonTicket(item.ticket)) return 2;
          return item.pass?.ticketClass === TicketClass.Adult ? 0 : 1;
        },
        // Secondary sort: by name or barcode
        (item) => item.pass?.name ?? item.ticket.barcode ?? '',
      ]);
    }

    // When there's no reservation, create tickets directly from passes
    // This is for attractions that don't require reservations
    if (passes.length > 0) {
      const ticketsFromPasses = passes.map((pass) => {
        // Check if the pass has a ticket for the current attraction
        const attractionTicket = attractionKey
          ? pass.tickets?.find((tk) => tk.attractionKey === attractionKey)
          : undefined;

        return {
          ticket: {
            attractionKey: attractionTicket?.attractionKey ?? '',
            barcode: attractionTicket?.barcode ?? pass.barcode,
            scan: attractionTicket?.scan ?? pass.scan ?? pass.barcode,
            display: attractionTicket?.display ?? pass.display ?? pass.barcode,
            passBarcode: pass.barcode,
          },
          pass,
        };
      });

      // Sort: adults first, then children, then by name
      return orderBy(ticketsFromPasses, [
        (item) => (item.pass?.ticketClass === TicketClass.Adult ? 0 : 1),
        (item) => item.pass?.name ?? item.ticket.barcode ?? '',
      ]);
    }

    return [];
  }, [reservationTickets, passes, showOrderBarcode, attractionKey]);

  const buttonWrapperStyle = useMemo(() => {
    if (Platform.OS !== 'web') return undefined;

    const controlsWidth = 80;

    // beside tickets
    if (rawWidth > MAX_WIDTH_WEB + controlsWidth) {
      return {
        marginLeft: -(controlsWidth / 2),
        width: MAX_WIDTH_WEB + controlsWidth,
      };
    }

    // below tickets
    return { top: maxTicketHeight ? maxTicketHeight / 2 : undefined };
  }, [maxTicketHeight, rawWidth]);

  const handleTicketLayout = (e: LayoutChangeEvent) => {
    const height = Math.ceil(e.nativeEvent.layout.height);
    if (height > maxTicketHeight) setMaxTicketHeight(height);
  };

  if (!productContent || (reservation && !attractionContent)) {
    return <Spinner testID={tID('TicketCarouselLoading')} />;
  }

  // Don't render Carousel with no children - causes infinite height bug in react-native-swiper
  if (allTickets.length === 0) {
    return <Box testID={tID('TicketCarousel')} />;
  }

  return (
    <Box testID={tID('TicketCarousel')}>
      <ResponsiveContainer maxWidth={600} padded={false} w="full">
        <Carousel
          buttonWrapperStyle={buttonWrapperStyle}
          loop={false}
          removeClippedSubviews={false}
          renderPagination={(index, total) => (
            <TicketsCarouselPagination cardHeight={maxTicketHeight} index={index} total={total} />
          )}
          showsButtons={Platform.OS === 'web'}
          style={{ height: maxTicketHeight || undefined }}
        >
          {allTickets.map((item, index) => {
            const { ticket, pass } = item;
            const ticketProps = {
              key: ticket.barcode ?? `ticket-${index}`,
              opacity: maxTicketHeight ? 1 : 0,
              px: '3',
              py: '2',
              w: 'full',
              onLayout: handleTicketLayout,
              ...createTicket({
                rt,
                ticket,
                pass,
                productContent,
                reservation,
                orderNumber,
                ticketCategory,
                attractionContentKey: reservation?.attractionConfig.contentKey ?? '',
                attractionContent,
                attractionConfig,
                familyCode: reservation?.productLineup.familyCode,
                t,
              }),
            };

            if (reservation && attractionContent && showOrderBarcode) {
              const adultCount = getPassCountByClass(passes, TicketClass.Adult);
              const childCount = getPassCountByClass(passes, TicketClass.Child);
              const addonCount =
                reservationTickets?.filter((tkt) => isAddonTicket(tkt)).length ?? 0;

              const travelPartyLabel =
                rt('order_barcode_travel_party', { adultCount, childCount }) ||
                `Adult (${adultCount}) Child (${childCount})`;
              const guestNameParts = [travelPartyLabel];
              if (addonCount > 0) {
                const addonLabel = getAddonLabel({
                  attractionConfig,
                  attractionContent,
                  product: productContent,
                  familyCode: reservation.productLineup.familyCode,
                  t,
                });
                guestNameParts.push(`${addonLabel} (${addonCount})`);
              }

              return (
                <ReservationTicket
                  {...ticketProps}
                  barcodeDisplay={reservation?.display ?? ''}
                  barcodeScan={reservation.scan ?? ''}
                  guestName={guestNameParts.join(' ')}
                  hideAge
                  // passBarcode prop receives ticket.barcode (ticket barcode) for wallet pass generation (even though we don't allow add to apple wallet  when we are showing an order level barcode)
                  passBarcode={ticket.barcode ?? ''}
                  reservation={reservation}
                  reservationDisplay={{
                    ...createReservationTicket({
                      attractionContent,
                      reservation,
                      ticket,
                      rt,
                      ticketCategory,
                    }),
                    hideReferenceNumber: true,
                  }}
                  showAddToWallet={false}
                />
              );
            }
            if (reservation && attractionContent) {
              return (
                <ReservationTicket
                  {...ticketProps}
                  // passBarcode prop receives ticket.barcode (ticket barcode) for wallet pass generation
                  passBarcode={ticket.barcode ?? ''}
                  reservation={reservation}
                  reservationDisplay={createReservationTicket({
                    attractionContent,
                    reservation,
                    ticket,
                    rt,
                    ticketCategory,
                  })}
                  showAddToWallet
                />
              );
            }
            return <Ticket {...ticketProps} />;
          })}
        </Carousel>
      </ResponsiveContainer>
      {/* <Dump data={{ passes, reservation }} /> */}
    </Box>
  );
}
